import React, { useState, useCallback } from 'react';
import { PaymentPlan, PromoCodeValidationResponse } from '../../types';
import { supabase } from '../../supabase/client';

interface PromoCodeInputProps {
  selectedPlan: PaymentPlan;
  originalAmount: number;
  onCodeApplied: (validation: PromoCodeValidationResponse) => void;
  onCodeRemoved: () => void;
  className?: string;
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  selectedPlan,
  originalAmount,
  onCodeApplied,
  onCodeRemoved,
  className = ''
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<PromoCodeValidationResponse | null>(null);
  const [showInput, setShowInput] = useState(false);

  const validatePromoCode = useCallback(async (promoCode: string) => {
    if (!promoCode.trim()) return;

    setIsValidating(true);
    try {
      const response = await fetch('https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/promo-code-validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          code: promoCode,
          planType: selectedPlan,
          amount: originalAmount
        })
      });

      if (!response.ok) {
        throw new Error('Failed to validate promo code');
      }

      const validation: PromoCodeValidationResponse = await response.json();
      setValidationResult(validation);

      if (validation.isValid) {
        onCodeApplied(validation);
      }
    } catch (error) {
      console.error('Promo code validation error:', error);
      setValidationResult({
        isValid: false,
        discountPercentage: 0,
        discountAmount: 0,
        finalAmount: originalAmount,
        stripePromotionCodeId: '',
        errorMessage: 'Failed to validate promo code. Please try again.'
      });
    } finally {
      setIsValidating(false);
    }
  }, [selectedPlan, originalAmount, onCodeApplied]);

  const handleApplyCode = async () => {
    await validatePromoCode(code);
  };

  const handleRemoveCode = () => {
    setCode('');
    setValidationResult(null);
    onCodeRemoved();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyCode();
    }
  };

  return (
    <div className={`promo-code-section ${className}`}>
      {!showInput && !validationResult ? (
        // Show promo code link when not active
        <button
          onClick={() => setShowInput(true)}
          className="text-purple-600 text-sm hover:text-purple-700 underline"
        >
          Have a promo code?
        </button>
      ) : (
        // Show promo code input and results
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center space-x-2 mb-2">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-1.414 0l-7-7A1.997 1.997 0 013 12V7a4 4 0 014-4z"></path>
            </svg>
            <span className="font-medium text-gray-900">Promo Code</span>
          </div>

          {validationResult?.isValid ? (
            // Show applied promo code
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span className="text-green-800 font-medium">Code "{code.toUpperCase()}" applied!</span>
                </div>
                <button
                  onClick={handleRemoveCode}
                  className="text-green-600 hover:text-green-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <div className="mt-2 text-sm text-green-700">
                {validationResult.discountPercentage}% discount applied 
                (${validationResult.discountAmount.toFixed(2)} off)
              </div>
            </div>
          ) : (
            // Show promo code input
            <div>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter promo code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isValidating}
                />
                <button
                  onClick={handleApplyCode}
                  disabled={!code.trim() || isValidating}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isValidating ? (
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Checking...</span>
                    </div>
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>

              {validationResult && !validationResult.isValid && (
                <div className="text-red-600 text-sm flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>{validationResult.errorMessage || 'Invalid promo code'}</span>
                </div>
              )}

              <div className="flex justify-between mt-2">
                <button
                  onClick={() => {
                    setShowInput(false);
                    setCode('');
                    setValidationResult(null);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
                <span className="text-xs text-gray-400">
                  Codes are case-insensitive
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;